import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/ui/sonner';
import { invalidateSlackStatus } from '@/entities/plugin';

const SLACK_RETURN_PARAM = 'slack';

// Handles the post-OAuth return: the backend redirects the browser to the UI
// with `?slack=connected` or `?slack=error`. We surface a toast, refresh the
// connection status, and strip the param so a refresh doesn't re-toast.
export function useSlackReturnHandler(enabled = true): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    let outcome: string | null;
    try {
      const params = new URLSearchParams(window.location.search);
      outcome = params.get(SLACK_RETURN_PARAM);
    } catch {
      return;
    }
    if (outcome !== 'connected' && outcome !== 'error') return;

    if (outcome === 'connected') {
      toast.success('Slack connected', { duration: 4000 });
      void invalidateSlackStatus(queryClient);
    } else {
      toast.error('Could not connect Slack. Please try again.', { duration: 4000 });
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(SLACK_RETURN_PARAM);
      window.history.replaceState(null, '', url.toString());
    } catch {
      // ignore URL cleanup failures — the toast already fired.
    }
    // Run once on mount; the param is removed so re-runs are no-ops anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
